import { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import Iconify from '../../../components/iconify';
import { bulkImportLeaves } from '../../../services/leaveService';

const TEMPLATE_HEADERS = ['email', 'leaveType', 'startDate', 'endDate', 'reason', 'isHalfDay', 'halfDayType'];
const TEMPLATE_SAMPLE = [
  ['alice@company.com', 'sick_emergency', `${new Date().getFullYear()}-01-10`, `${new Date().getFullYear()}-01-11`, 'Flu', 'false', ''],
  ['bob@company.com', 'personal', `${new Date().getFullYear()}-02-14`, `${new Date().getFullYear()}-02-14`, 'Personal errand', 'true', 'first_half'],
];

function downloadTemplate() {
  const rows = [TEMPLATE_HEADERS, ...TEMPLATE_SAMPLE];
  const csv = rows.map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'leave_bulk_import_template.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function LeaveBulkImport() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);
  const currentYear = new Date().getFullYear();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setImportSummary(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }
    try {
      setIsUploading(true);
      const summary = await bulkImportLeaves(selectedFile);
      setImportSummary(summary);
      if (summary.failed === 0) {
        toast.success(`Import complete — ${summary.success} record${summary.success !== 1 ? 's' : ''} imported. Result report downloaded.`);
      } else if (summary.success === 0) {
        toast.error(`Import failed — all ${summary.failed} row${summary.failed !== 1 ? 's' : ''} had errors. Check the downloaded report.`);
      } else {
        toast.warning(`Partial import — ${summary.success} succeeded, ${summary.failed} failed. Check the downloaded report.`);
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Error processing import file';
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Bulk Import Historical Leaves — {currentYear}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Upload an Excel (.xlsx) or CSV file to backfill missing leave records for the current year ({currentYear}).
          All imported records will be saved as <strong>approved</strong> historical leaves.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Required columns: <strong>email</strong>, <strong>leaveType</strong> (<code>sick_emergency</code> or <code>personal</code>),{' '}
          <strong>startDate</strong>, <strong>endDate</strong> (YYYY-MM-DD). Optional: <strong>reason</strong>,{' '}
          <strong>isHalfDay</strong>, <strong>halfDayType</strong> (<code>first_half</code> / <code>second_half</code>).
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Dates outside {currentYear}, duplicates, overlapping leaves, and weekends/holidays will be rejected per row.
          A detailed result report (.xlsx) will be downloaded automatically after processing.
        </Alert>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Iconify icon="eva:download-fill" />}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>

          <Button
            variant="outlined"
            component="label"
            startIcon={<Iconify icon="eva:attach-fill" />}
          >
            {selectedFile ? selectedFile.name : 'Choose File'}
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </Button>

          <LoadingButton
            variant="contained"
            startIcon={<Iconify icon="eva:upload-fill" />}
            onClick={handleUpload}
            loading={isUploading}
            disabled={!selectedFile}
          >
            Import
          </LoadingButton>
        </Stack>

        {importSummary && (
          <Box sx={{ mt: 2 }}>
            {importSummary.failed === 0 ? (
              <Alert severity="success">
                {importSummary.success} record{importSummary.success !== 1 ? 's' : ''} imported successfully.
                The result report has been downloaded.
              </Alert>
            ) : importSummary.success === 0 ? (
              <Alert severity="error">
                All {importSummary.failed} row{importSummary.failed !== 1 ? 's' : ''} failed validation.
                No records were imported. Check the downloaded report for details.
              </Alert>
            ) : (
              <Alert severity="warning">
                {importSummary.success} record{importSummary.success !== 1 ? 's' : ''} imported,{' '}
                {importSummary.failed} row{importSummary.failed !== 1 ? 's' : ''} failed.
                Check the downloaded report for per-row error details.
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
